import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import Pagination from "../common/Pagination";
import StatusBadge from "./StatusBadge";
import { listTemplates, isAgentNotConfigured } from "./rcsTemplateApi";
import { TEMPLATE_TYPES, displayFormattedDate, typeLabel } from "./templatePayload";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "pending_review", label: "Pending review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const selectClass =
  "px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#00b6ce]/20 focus:border-[#00b6ce]";

const TemplateList = ({ nav, onAgentMissing, searchParams }) => {
  const status = searchParams.get("status") || "";
  const type = searchParams.get("type") || "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const res = await listTemplates({ status, type, page, page_size: PAGE_SIZE });
      if (cancelled) return;
      setLoading(false);
      if (res.ok) {
        setTemplates(res.data.data || []);
        setTotal(res.data.total || 0);
      } else if (isAgentNotConfigured(res)) {
        onAgentMissing();
      } else {
        setTemplates([]);
        setTotal(0);
        Swal.fire({ title: "Couldn't load templates", text: res.error, icon: "error" });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [status, type, page, onAgentMissing]);

  const setFilters = (next) => {
    const merged = { status, type, page: String(page), ...next };
    nav.list(
      Object.fromEntries(
        Object.entries(merged).filter(([k, v]) => v !== "" && !(k === "page" && v === "1"))
      )
    );
  };

  const hasFilters = status || type;

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">RCS Templates</h1>
        <button
          onClick={() => nav.create()}
          className="flex items-center gap-2 bg-[#00b6ce] hover:bg-[#009baf] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm"
        >
          <span>+</span> Create template
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={status}
          onChange={(e) => setFilters({ status: e.target.value, page: "1" })}
          className={selectClass}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => setFilters({ type: e.target.value, page: "1" })}
          className={selectClass}
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          {TEMPLATE_TYPES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {hasFilters && (
          <button
            onClick={() => setFilters({ status: "", type: "", page: "1" })}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-gray-200 text-xs font-bold text-[#00b6ce] uppercase tracking-wider text-left">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Last checked</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-16">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00b6ce]" />
                  </div>
                </td>
              </tr>
            ) : templates.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-16 text-center text-gray-500">
                  {hasFilters ? "No templates match these filters" : "No templates yet"}
                </td>
              </tr>
            ) : (
              templates.map((t) => (
                <tr key={t.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-700 max-w-[260px] truncate">
                    <button
                      onClick={() => nav.detail(t.id)}
                      className="hover:text-[#00b6ce] text-left truncate max-w-full"
                    >
                      {t.template_name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{typeLabel(t.template_type)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {displayFormattedDate(t.inserted_at)}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {displayFormattedDate(t.last_status_checked_at) || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => nav.detail(t.id)}
                        className="px-3 py-1 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#00b6ce] hover:text-[#00b6ce]"
                      >
                        View
                      </button>
                      {t.status === "approved" && (
                        <button
                          onClick={() => nav.send(t.id)}
                          className="px-3 py-1 rounded-lg bg-[#00b6ce] hover:bg-[#009baf] text-xs font-semibold text-white"
                        >
                          Send
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="mt-4">
          <Pagination
            currentPage={page}
            totalCount={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setFilters({ page: String(p) })}
          />
        </div>
      )}
    </div>
  );
};

export default TemplateList;
